import type { DinoBackendClient } from "../../services/backend/supabaseClient.js";
import type { Json } from "../../services/backend/database.types.js";
import type { Level, SearchResults } from "../../types/global.js";

type Row = { content_type: string; content_key: string; result: Json };

function object(value: Json | undefined): Record<string, Json | undefined> {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value : {};
}

function string(value: Json | undefined): string {
    return typeof value === "string" ? value : "";
}

/** Search uses published revisions only; the RPC never returns admin drafts. */
async function searchPublishedContent(
    client: DinoBackendClient,
    query: string
): Promise<SearchResults> {
    const { data, error } = await client.rpc("search_published_content", {
        p_query: query.trim()
    });
    if (error) throw error;
    return mapPublishedSearchRows(data);
}

function mapPublishedSearchRows(rows: readonly Row[]): SearchResults {
    const results: SearchResults = { vocab: [], grammar: [], news: [], travel: [] };
    for (const row of rows) {
        const payload = object(row.result);
        if (row.content_type === "vocabulary_pack") {
            const word = object(payload.word);
            const catalog = object(payload.catalog);
            if (!string(word.fr) || !string(word.fa)) continue;
            results.vocab.push({
                fr: string(word.fr), fa: string(word.fa),
                ex: string(word.ex), ex_fa: string(word.ex_fa),
                emoji: string(word.emoji),
                level: string(catalog.level) as Level,
                packId: row.content_key,
                packTitle: string(catalog.title),
                packTitleFa: string(catalog.title_fa)
            });
        } else if (row.content_type === "grammar_lesson") {
            results.grammar.push({
                id: row.content_key,
                level: string(payload.level) as Level,
                module: string(payload.module),
                category: string(payload.category),
                icon: string(payload.icon),
                title: string(payload.title),
                title_fa: string(payload.title_fa),
                estimatedTime: Number(payload.estimatedTime) || 0,
                exercises: Number(payload.exercises) || 0
            });
        } else if (row.content_type === "news_article") {
            results.news.push({
                id: row.content_key,
                title: string(payload.title),
                title_fa: string(payload.title_fa),
                subtitle: string(payload.subtitle),
                subtitle_fa: string(payload.subtitle_fa),
                image: string(payload.image),
                level: string(payload.level),
                publishedDate: string(payload.publishedDate)
            });
        } else if (row.content_type === "travel_lesson") {
            results.travel!.push({
                id: row.content_key,
                title: string(payload.title),
                title_fa: string(payload.title_fa),
                icon: string(payload.icon),
                estimatedTime: Number(payload.estimatedTime) || 0
            });
        }
    }
    return results;
}

export { mapPublishedSearchRows, searchPublishedContent };
